import { Flex, Heading, Stack, StackDivider } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Option, Select } from '@main/complykit';
import { Card } from '@main/complykit/components/card';
import { Content, Footer, Header } from '@main/complykit/components/slots';
import { Integration_Names_Enum } from '@main/graphql/types.generated';
import { Promisable } from '@main/shared/types';
import { useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod/v4';

import { PolicyConfigRef, PolicyIntegrationConfig } from '../policy-integration-config';

export type AddPolicyHandlerCardProps = {
  connectors: ConnectorOptionInput[];
  isReadOnly?: boolean;
  onAdd: (payload: AddPolicyHandlerPayload) => Promisable<void>;
};

type ConnectorOptionInput = {
  id: string;
  providerName: Integration_Names_Enum;
  name?: string;
};

export type AddPolicyHandlerPayload = {
  connectorId: string;
  config: unknown;
};

const formSchema = z.object({
  connectorId: z
    .string({ error: 'addHandlerCard.form.validation.connector' })
    .min(1, { message: 'addHandlerCard.form.validation.connector' }),
});

export const AddPolicyHandlerCard = ({
  connectors,
  isReadOnly = false,
  onAdd,
}: AddPolicyHandlerCardProps) => {
  const { t } = useTranslation('integrations');

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      connectorId: '',
    },
  });

  const connectorOptions = useConnectorOptions(connectors);
  const currentConnectorId = form.watch('connectorId');
  const currentProviderName = connectors.find(
    (connector) => connector.id === currentConnectorId,
  )?.providerName;

  const configRef = useRef<PolicyConfigRef>(null);

  return (
    <Card>
      <Header>
        <Heading size="sm">{t('addPolicyHandlerCard.title')}</Heading>
      </Header>

      <Content>
        <Stack spacing={4} divider={<StackDivider />}>
          <Controller
            control={form.control}
            name="connectorId"
            render={({ field, fieldState }) => (
              <Select
                ref={field.ref}
                label={t('addPolicyHandlerCard.form.connectorId.label')}
                isClearable
                clearLabel={t('clearSelection', {
                  name: t('addPolicyHandlerCard.form.connectorId.label'),
                })}
                name={field.name}
                value={field.value || null}
                onChange={(id) => field.onChange(id ?? undefined)}
                onBlur={field.onBlur}
                isDisabled={form.formState.isSubmitting}
                isReadOnly={isReadOnly}
                error={fieldState.error?.message && t(fieldState.error.message as never)}
                placeholder={t('addPolicyHandlerCard.form.connectorId.placeholder')}
                items={connectorOptions}
                emptyMessage={t('addPolicyHandlerCard.form.connectorId.empty')}
              >
                {(option) => <Option id={option.value}>{option.label}</Option>}
              </Select>
            )}
          />

          {currentProviderName && currentConnectorId && (
            <PolicyIntegrationConfig
              ref={configRef}
              providerName={currentProviderName}
              orgIntegrationId={currentConnectorId}
              isDisabled={form.formState.isSubmitting || isReadOnly}
            />
          )}
        </Stack>
      </Content>

      <Footer>
        <Flex justify="end" w="full">
          <Button
            isDisabled={isReadOnly}
            isPending={form.formState.isSubmitting}
            onPress={() =>
              void form.handleSubmit(async ({ connectorId }) => {
                const config = await configRef.current?.submit();
                if (!config) return;
                await onAdd({ connectorId, config });
              })()
            }
          >
            {t('addPolicyHandlerCard.form.submitBtn')}
          </Button>
        </Flex>
      </Footer>
    </Card>
  );
};

function useConnectorOptions(connectors: ConnectorOptionInput[]) {
  const { t } = useTranslation('integrations');

  return useMemo(() => {
    return connectors
      .map((connector) => {
        const label = [
          t(`integrations.${connector.providerName}.name`),
          connector.name ? `(${connector.name})` : null,
        ]
          .filter(Boolean)
          .join(' ');
        return {
          value: connector.id,
          label,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [connectors, t]);
}
